import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api, withQuery, money, date } from '../api.js';
import Lookup from '../components/Lookup.jsx';
import ProductLookup from '../components/ProductLookup.jsx';
import GroupPicker from '../components/GroupPicker.jsx';
import DateField from '../components/DateField.jsx';
import { useAuth } from '../App.jsx';

/**
 * Ценови листи и специални цени — като екрана в Генсофт.
 *
 * Горе е филтърът (листа, фирма, група, стока), под него две таблици:
 * правилата на ценовите листи и специалните цени (на фирма или за всички
 * клиенти). Едно правило = за кого + за какво + от колко бройки + как.
 * Праговете по количество са няколко правила за едно и също нещо.
 *
 * Засега само описание (етап 1) — цената в продажбата още не се мени
 * от тях.
 */

export const KIND_LABEL = {
  fixed: 'твърда цена',
  discount_pct: '% отстъпка',
  markup_pct: '% надценка',
  discount_amount: 'отстъпка в пари',
  markup_amount: 'надценка в пари',
};
const LEVEL_LABEL = { retail: 'дребно', wholesale: 'едро' };

/** Правилото с думи: „9,20 €", „−10 % от дребно", „+2,00 € от едро". */
export function praviloText(r) {
  const v = Number(r.value);
  const ot = r.base_level ? ` от ${LEVEL_LABEL[r.base_level]}` : '';
  switch (r.kind) {
    case 'fixed': return `${money(v)} · ${money(Math.round(v * 1.2 * 100) / 100)} с ДДС`;
    case 'discount_pct': return `−${v.toLocaleString('bg-BG')} %${ot}`;
    case 'markup_pct': return `+${v.toLocaleString('bg-BG')} %${ot}`;
    case 'discount_amount': return `−${money(v)}${ot}`;
    case 'markup_amount': return `+${money(v)}${ot}`;
    default: return String(v);
  }
}

/** Цената днес за правило по стока — за сверка на око. */
function cenaDnes(r) {
  if (!r.product_id) return null;
  if (r.kind === 'fixed') return Number(r.value);
  const baza = Number((r.base_level === 'wholesale' ? r.wholesale_price : r.retail_price)
    ?? r.retail_price ?? 0);
  if (!baza) return null;
  const v = Number(r.value);
  switch (r.kind) {
    case 'discount_pct': return baza * (1 - v / 100);
    case 'markup_pct': return baza * (1 + v / 100);
    case 'discount_amount': return baza - v;
    case 'markup_amount': return baza + v;
    default: return null;
  }
}

export default function PriceLists() {
  const { user } = useAuth();
  const canWrite = user.role !== 'auditor';
  const [params, setParams] = useSearchParams();

  const f = {
    priceListId: params.get('priceListId') ?? '',
    partnerId: params.get('partnerId') ?? '',
    groupId: params.get('groupId') ?? '',
    productId: params.get('productId') ?? '',
    active: params.get('active') ?? '',
  };
  function change(patch) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === '' || v == null) next.delete(k); else next.set(k, String(v));
    }
    setParams(next, { replace: true });
  }

  const [lists, setLists] = useState([]);
  const [groups, setGroups] = useState([]);
  const [names, setNames] = useState({ partner: '', product: '' });
  const [listRules, setListRules] = useState([]);
  const [special, setSpecial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // правило за редакция или {} за ново
  const [listsOpen, setListsOpen] = useState(false);

  const loadLists = () => api.get('/api/price-lists').then(setLists).catch(() => {});
  useEffect(() => {
    loadLists();
    api.get('/api/product-groups').then(setGroups).catch(() => {});
  }, []);

  // Името на избраната фирма/стока, когато е дошла от адреса (картона на клиента).
  useEffect(() => {
    if (f.partnerId && !names.partner) {
      api.get(`/api/partners/${f.partnerId}`)
        .then((p) => setNames((n) => ({ ...n, partner: p.name }))).catch(() => {});
    }
    if (f.productId && !names.product) {
      api.get(`/api/products/${f.productId}`)
        .then((p) => setNames((n) => ({ ...n, product: p.name ?? p.product?.name ?? '' })))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.partnerId, f.productId]);

  function load() {
    setLoading(true);
    setError(null);
    const q = {
      priceListId: f.priceListId || undefined,
      partnerId: f.partnerId || undefined,
      groupId: f.groupId || undefined,
      productId: f.productId || undefined,
      active: f.active || undefined,
    };
    /* Филтър по фирма значи „специалните ѝ цени" — листите тогава не се
       показват; по листа — обратното. */
    const iskaListi = !f.partnerId;
    const iskaSpec = !f.priceListId;
    Promise.all([
      iskaListi ? api.get(withQuery('/api/price-rules', { ...q, scope: 'lists' })) : [],
      iskaSpec ? api.get(withQuery('/api/price-rules', { ...q, scope: 'special' })) : [],
    ])
      .then(([a, b]) => { setListRules(a); setSpecial(b); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [f.priceListId, f.partnerId, f.groupId, f.productId, f.active]);

  const groupName = (id) => groups.find((g) => String(g.id) === String(id))?.name;

  async function iztrii(r) {
    if (!window.confirm(`Изтриване на правилото „${praviloText(r)}"?`)) return;
    try { await api.del(`/api/price-rules/${r.id}`); load(); } catch (e) { setError(e.message); }
  }

  const novoPravilo = () => setEditing({
    whom: f.partnerId ? 'partner' : f.priceListId ? 'list' : 'partner',
    price_list_id: f.priceListId ? Number(f.priceListId) : null,
    partner_id: f.partnerId ? Number(f.partnerId) : null,
    partner_name: names.partner,
    group_id: f.groupId ? Number(f.groupId) : null,
    product_id: f.productId ? Number(f.productId) : null,
    product_name: names.product,
    min_qty: 0, kind: 'fixed', value: '', base_level: null, is_active: true,
  });

  return (
    <>
      <div className="page-head">
        <h1>Ценови листи и специални цени</h1>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="card">
        <div className="card-body">
          <div className="toolbar" style={{ marginBottom: 0 }}>
            <label className="field">
              Ценова листа
              <select value={f.priceListId} onChange={(e) => change({ priceListId: e.target.value })}>
                <option value="">всички</option>
                {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </label>
            <div className="field" style={{ minWidth: 240 }}>
              Фирма
              <Lookup url="/api/partners" params={{ type: 'client' }} value={f.partnerId || null}
                label={names.partner} placeholder="всички"
                onPick={(id, name) => { setNames((n) => ({ ...n, partner: name ?? '' })); change({ partnerId: id ?? '' }); }} />
            </div>
            <div className="field">
              Група
              <GroupPicker label="всички" value={f.groupId || null} valueLabel={groupName(f.groupId)}
                onPick={(id) => change({ groupId: id ?? '' })} onClear={() => change({ groupId: '' })} />
            </div>
            <div className="field" style={{ minWidth: 240 }}>
              Стока
              <ProductLookup value={f.productId || null} label={names.product} placeholder="всички"
                onPick={(id, name) => { setNames((n) => ({ ...n, product: name ?? '' })); change({ productId: id ?? '' }); }} />
            </div>
            <label className="check">
              <input type="checkbox" checked={f.active === '1'}
                onChange={(e) => change({ active: e.target.checked ? '1' : '' })} />
              само активните
            </label>
            <div className="spacer" />
            <button onClick={() => setListsOpen(true)}>Ценови листи…</button>
            {canWrite && <button className="primary" onClick={novoPravilo}>+ Правило</button>}
          </div>
        </div>
      </div>

      {!f.partnerId && (
        <RuleTable
          title="Правила на ценовите листи"
          hint="важат за всички клиенти, закачени към листата"
          rows={listRules} loading={loading} canWrite={canWrite} groupName={groupName}
          whoLabel={(r) => r.price_list_name}
          onEdit={(r) => setEditing({ ...r, whom: 'list' })} onDelete={iztrii}
        />
      )}

      {!f.priceListId && (
        <RuleTable
          title="Специални цени"
          hint="на отделна фирма или за всички клиенти — бият правилата на листата"
          rows={special} loading={loading} canWrite={canWrite} groupName={groupName}
          whoLabel={(r) => r.partner_name ?? <span className="badge">всички клиенти</span>}
          onEdit={(r) => setEditing({ ...r, whom: r.partner_id ? 'partner' : 'all' })} onDelete={iztrii}
        />
      )}

      {editing && (
        <RuleModal rule={editing} lists={lists} groups={groups}
          onClose={() => setEditing(null)}
          onSaved={(again) => { load(); if (!again) setEditing(null); }} />
      )}

      {listsOpen && (
        <ListsModal lists={lists} canWrite={canWrite}
          onChanged={loadLists} onClose={() => setListsOpen(false)} />
      )}
    </>
  );
}

function RuleTable({ title, hint, rows, loading, canWrite, groupName, whoLabel, onEdit, onDelete }) {
  return (
    <div className="card">
      <div className="card-head">
        {title} <span className="muted" style={{ fontWeight: 400, marginLeft: 8 }}>{hint}</span>
        {loading && <span className="muted" style={{ marginLeft: 8 }}>зареждане…</span>}
      </div>
      <div className="table-wrap">
        <table className="compact">
          <thead>
            <tr>
              <th>За</th>
              <th>Група</th>
              <th>Стока</th>
              <th className="num">От к-во</th>
              <th>Правило</th>
              <th className="num">Цена днес</th>
              <th>Срок</th>
              <th>Бележка</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const dnes = cenaDnes(r);
              return (
                <tr key={r.id} className={r.is_active ? undefined : 'muted'}>
                  <td>{whoLabel(r)}</td>
                  <td className="muted">
                    {r.group_id ? <strong>{r.group_name} <span className="muted">(с подгрупите)</span></strong>
                      : r.product_id ? r.product_group_name : 'всички стоки'}
                  </td>
                  <td>{r.product_name ?? ''}{r.product_code ? <span className="muted"> · {r.product_code}</span> : null}</td>
                  <td className="num">{Number(r.min_qty) > 0 ? `от ${Number(r.min_qty).toLocaleString('bg-BG')}` : '—'}</td>
                  <td><strong>{praviloText(r)}</strong>{!r.is_active && <span className="badge muted" style={{ marginLeft: 6 }}>неактивно</span>}</td>
                  <td className="num muted">{dnes == null ? '' : money(dnes)}</td>
                  <td className="muted">
                    {r.valid_from || r.valid_to
                      ? `${r.valid_from ? date(r.valid_from) : '…'} – ${r.valid_to ? date(r.valid_to) : '…'}`
                      : ''}
                  </td>
                  <td className="muted">{r.note}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {canWrite && (
                      <>
                        <button className="link" onClick={() => onEdit(r)}>редакция</button>
                        <button className="link" style={{ marginLeft: 10, color: 'var(--danger)' }}
                          onClick={() => onDelete(r)}>изтрий</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="empty">Няма правила</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Ново правило или редакция — „За кого", „За какво", „Как". */
function RuleModal({ rule, lists, groups, onClose, onSaved }) {
  const [whom, setWhom] = useState(rule.whom ?? 'partner');
  const [listId, setListId] = useState(rule.price_list_id ?? '');
  const [partner, setPartner] = useState({ id: rule.partner_id ?? null, name: rule.partner_name ?? '' });
  const [what, setWhat] = useState(rule.product_id ? 'product' : rule.group_id ? 'group' : 'all');
  const [groupId, setGroupId] = useState(rule.group_id ?? null);
  const [product, setProduct] = useState({ id: rule.product_id ?? null, name: rule.product_name ?? '' });
  const [minQty, setMinQty] = useState(String(rule.min_qty ?? 0));
  const [kind, setKind] = useState(rule.kind ?? 'fixed');
  const [value, setValue] = useState(rule.value === '' || rule.value == null ? '' : String(rule.value));
  const [gross, setGross] = useState(null); // пише се в полето „с ДДС" — пази се, докато се пише
  const [baseLevel, setBaseLevel] = useState(rule.base_level ?? '');
  const [validFrom, setValidFrom] = useState(rule.valid_from ? String(rule.valid_from).slice(0, 10) : '');
  const [validTo, setValidTo] = useState(rule.valid_to ? String(rule.valid_to).slice(0, 10) : '');
  const [isActive, setIsActive] = useState(rule.is_active !== false);
  const [note, setNote] = useState(rule.note ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const groupName = useMemo(
    () => groups.find((g) => String(g.id) === String(groupId))?.name, [groups, groupId]);

  async function zapishi(again) {
    setError(null);
    if (whom === 'list' && !listId) { setError('Избери ценова листа'); return; }
    if (whom === 'partner' && !partner.id) { setError('Избери фирма'); return; }
    if (what === 'group' && !groupId) { setError('Избери група'); return; }
    if (what === 'product' && !product.id) { setError('Избери стока'); return; }
    if (value === '' || Number.isNaN(Number(value))) { setError('Въведи стойност'); return; }
    const body = {
      priceListId: whom === 'list' ? Number(listId) : null,
      partnerId: whom === 'partner' ? partner.id : null,
      groupId: what === 'group' ? Number(groupId) : null,
      productId: what === 'product' ? product.id : null,
      minQty: Number(minQty || 0),
      kind,
      value: Number(String(value).replace(',', '.')),
      baseLevel: kind === 'fixed' ? null : (baseLevel || null),
      validFrom: validFrom || null,
      validTo: validTo || null,
      isActive,
      note: note.trim() || null,
    };
    setBusy(true);
    try {
      if (rule.id) await api.put(`/api/price-rules/${rule.id}`, body);
      else await api.post('/api/price-rules', body);
      onSaved(again);
      if (again) {
        // „Запиши и още" — същото „за кого/за какво", нов праг/стойност.
        setMinQty('');
        setValue('');
        setGross(null);
        setNote('');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth: 640 }}>
        <div className="card-head">{rule.id ? 'Редакция на правило' : 'Ново правило'}</div>
        <div className="card-body">
          {error && <div className="error">{error}</div>}

          <div className="field">За кого</div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', margin: '4px 0 8px' }}>
            {[['partner', 'една фирма'], ['list', 'ценова листа'], ['all', 'всички клиенти']].map(([k, l]) => (
              <label key={k} className="check">
                <input type="radio" name="whom" checked={whom === k} onChange={() => setWhom(k)} /> {l}
              </label>
            ))}
          </div>
          {whom === 'partner' && (
            <Lookup url="/api/partners" params={{ type: 'client' }} value={partner.id}
              label={partner.name} placeholder="търсене по име или ЕИК"
              onPick={(id, name) => setPartner({ id, name: name ?? '' })} />
          )}
          {whom === 'list' && (
            <select value={listId} onChange={(e) => setListId(e.target.value)}>
              <option value="">— избери листа —</option>
              {lists.filter((l) => l.is_active || String(l.id) === String(listId))
                .map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}

          <div className="field" style={{ marginTop: 14 }}>За какво</div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', margin: '4px 0 8px' }}>
            {[['product', 'стока'], ['group', 'група (с подгрупите)'], ['all', 'всички стоки']].map(([k, l]) => (
              <label key={k} className="check">
                <input type="radio" name="what" checked={what === k} onChange={() => setWhat(k)} /> {l}
              </label>
            ))}
          </div>
          {what === 'product' && (
            <ProductLookup value={product.id} label={product.name}
              onPick={(id, name) => setProduct({ id, name: name ?? '' })} />
          )}
          {what === 'group' && (
            <GroupPicker label="избери група" value={groupId} valueLabel={groupName}
              onPick={(id) => setGroupId(id)} onClear={() => setGroupId(null)} />
          )}

          <div className="form-grid" style={{ marginTop: 14 }}>
            <label className="field">
              От количество
              <input className="num" inputMode="decimal" value={minQty} placeholder="0 = без значение"
                onChange={(e) => setMinQty(e.target.value)} />
            </label>
            <label className="field">
              Вид
              <select value={kind} onChange={(e) => setKind(e.target.value)}>
                {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <label className="field">
              {kind.endsWith('_pct') ? 'Процент' : 'Сума, € (без ДДС)'}
              <input className="num" inputMode="decimal" value={value} autoComplete="off"
                onChange={(e) => { setValue(e.target.value); setGross(null); }} />
            </label>
            {kind === 'fixed' && (
              /* Кръгла цена с ДДС се пише направо — без ДДС се смята назад с
                 4 знака (4,00 € -> 3,3333 €), иначе сборовете се разминават. */
              <label className="field">
                Цена с ДДС (20 %)
                <input className="num" inputMode="decimal" autoComplete="off"
                  value={gross ?? (value === '' ? '' : (Math.round(Number(String(value).replace(',', '.')) * 1.2 * 100) / 100).toFixed(2))}
                  onChange={(e) => {
                    const g = e.target.value;
                    setGross(g);
                    const n = Number(g.replace(',', '.'));
                    setValue(g === '' || Number.isNaN(n) ? '' : String(Math.round((n / 1.2) * 10000) / 10000));
                  }} />
              </label>
            )}
            {kind !== 'fixed' && (
              <label className="field">
                От цена
                <select value={baseLevel} onChange={(e) => setBaseLevel(e.target.value)}>
                  <option value="">по нивото на клиента / листата</option>
                  <option value="retail">на дребно</option>
                  <option value="wholesale">на едро</option>
                </select>
              </label>
            )}
            <label className="field">
              От дата
              <DateField value={validFrom} onChange={setValidFrom} />
            </label>
            <label className="field">
              До дата
              <DateField value={validTo} onChange={setValidTo} />
            </label>
          </div>

          <label className="field" style={{ marginTop: 10 }}>
            Бележка
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} />
          </label>
          <label className="check" style={{ marginTop: 10 }}>
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            активно
          </label>
        </div>
        <div className="modal-foot">
          <button onClick={onClose} disabled={busy}>Отказ</button>
          {!rule.id && <button onClick={() => zapishi(true)} disabled={busy}>Запиши и още</button>}
          <button className="primary" onClick={() => zapishi(false)} disabled={busy}>Запиши</button>
        </div>
      </div>
    </div>
  );
}

/** Ценовите листи — име, от коя цена тръгват, активна ли е. */
function ListsModal({ lists, canWrite, onChanged, onClose }) {
  const [name, setName] = useState('');
  const [base, setBase] = useState('retail');
  const [error, setError] = useState(null);

  async function dobavi() {
    if (!name.trim()) return;
    setError(null);
    try {
      await api.post('/api/price-lists', { name: name.trim(), baseLevel: base });
      setName('');
      onChanged();
    } catch (e) { setError(e.message); }
  }

  async function smeni(l, patch) {
    setError(null);
    try { await api.put(`/api/price-lists/${l.id}`, patch); onChanged(); } catch (e) { setError(e.message); }
  }

  return (
    <div className="modal-backdrop">
      <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth: 640 }}>
        <div className="card-head">Ценови листи</div>
        <div className="card-body">
          {error && <div className="error">{error}</div>}
          <div className="table-wrap">
            <table className="compact">
              <thead>
                <tr><th>Име</th><th>От цена</th><th className="num">Клиенти</th><th className="num">Правила</th><th /></tr>
              </thead>
              <tbody>
                {lists.map((l) => (
                  <tr key={l.id} className={Number(l.is_active) ? undefined : 'muted'}>
                    <td>
                      <Link to={`/price-lists?priceListId=${l.id}`} onClick={onClose}>{l.name}</Link>
                    </td>
                    <td>
                      <select value={l.base_level} disabled={!canWrite}
                        onChange={(e) => smeni(l, { baseLevel: e.target.value })}>
                        <option value="retail">на дребно</option>
                        <option value="wholesale">на едро</option>
                      </select>
                    </td>
                    <td className="num">{l.partners}</td>
                    <td className="num">{l.rules}</td>
                    <td>
                      {canWrite && (
                        <button className="link" onClick={() => smeni(l, { isActive: !Number(l.is_active) })}>
                          {Number(l.is_active) ? 'спри' : 'пусни'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {lists.length === 0 && (
                  <tr><td colSpan={5} className="empty">Още няма ценови листи</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {canWrite && (
            <div className="toolbar" style={{ marginTop: 12, marginBottom: 0 }}>
              <label className="field" style={{ flex: 1 }}>
                Нова листа
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="напр. Дистрибутори"
                  autoComplete="off" onKeyDown={(e) => { if (e.key === 'Enter') dobavi(); }} />
              </label>
              <label className="field">
                От цена
                <select value={base} onChange={(e) => setBase(e.target.value)}>
                  <option value="retail">на дребно</option>
                  <option value="wholesale">на едро</option>
                </select>
              </label>
              <button className="primary" onClick={dobavi}>Добави</button>
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button onClick={onClose}>Затвори</button>
        </div>
      </div>
    </div>
  );
}
